/**
 * Classement Elo VIVANT (specs/03 §1) : les 48 équipes, Elo courant recalculé à
 * chaque match terminé (officialStore applique la formule officielle après chaque
 * résultat) + Δ depuis le 11/06, + la NOTE d'équipe moyenne (sur le tournoi) + la
 * VALEUR d'effectif (Transfermarkt). Table TRIABLE (clic sur l'en-tête Elo, Note ou
 * Valeur) avec une ligne « moyenne du tournoi » en pied.
 */
import { t } from "../../app/i18n.ts";
import { teams, initialElos } from "../../app/data.ts";
import type { OfficialState } from "../../app/officialStore.ts";
import { flagSpan } from "../html.ts";
import { currentSquadValues, refreshValueElos } from "../../app/valueModel.ts";
import { ensureTeamRatings, teamTournamentRatings } from "../../app/teamRatings.ts";
import { createSortState, cycleSort, sortRows, sortClass, sortMark, sortAria } from "./tableSort.ts";

const ADJUSTED = new Set(teams.filter((t) => t.eloFlags.includes("adjusted")).map((t) => t.id));
const fmtValue = (v: number): string =>
  v > 0 ? t("elo.value.millions", { n: Math.round(v / 1e6).toLocaleString("fr-FR") }) : "—";

type SortKey = "elo" | "note" | "value";
// Tri à deux niveaux (primaire + secondaire de départage), cf. tableSort.ts.
const sort = createSortState<SortKey>("elo");
let lastEl: HTMLElement | null = null;
let lastState: OfficialState | null = null;
let refreshed = false;

/** Clic d'en-tête → met à jour le tri (primaire/secondaire) puis re-rend en place. */
function applySort(key: SortKey): void {
  cycleSort(sort, key);
  if (lastEl && lastState) renderEloRanking(lastEl, lastState);
}

export function renderEloRanking(el: HTMLElement, state: OfficialState): void {
  lastEl = el;
  lastState = state;
  // À la 1re pose : récupère les valeurs d'effectif fraîches puis re-rend une fois
  // (la graine committée s'affiche immédiatement en attendant — règle 8).
  if (!refreshed) {
    refreshed = true;
    void Promise.all([refreshValueElos(), ensureTeamRatings()]).then(() => {
      if (lastEl && lastState) renderEloRanking(lastEl, lastState);
    });
  }

  const values = currentSquadValues();
  const notes = teamTournamentRatings();
  const rows = teams.map((t) => {
    const elo = Math.round(state.currentElos[t.id] ?? t.elo);
    const base = Math.round(initialElos[t.id] ?? t.elo);
    return {
      code: t.id,
      elo,
      delta: elo - base,
      value: values[t.id] ?? 0,
      note: notes[t.id]?.avg ?? null,
      noteN: notes[t.id]?.n ?? 0,
    };
  });

  const avgElo = Math.round(rows.reduce((a, r) => a + r.elo, 0) / (rows.length || 1));
  const valued = rows.filter((r) => r.value > 0);
  const avgValue = valued.length ? valued.reduce((a, r) => a + r.value, 0) / valued.length : 0;
  const noted = rows.filter((r) => r.note != null);
  const avgNote = noted.length
    ? noted.reduce((a, r) => a + (r.note as number), 0) / noted.length
    : null;

  const metric = (r: (typeof rows)[number], k: SortKey): number =>
    k === "value" ? r.value : k === "note" ? (r.note ?? -1) : r.elo;
  const sorted = sortRows(rows, sort, metric, (a, b) => a.code.localeCompare(b.code));
  const playedCount = rows.filter((r) => r.delta !== 0).length;

  const th = (k: SortKey, label: string): string => {
    const cls = sortClass(sort, k);
    return `<th class="elo-th-sort${cls ? ` ${cls}` : ""}" data-sort="${k}" role="button" tabindex="0" aria-sort="${sortAria(sort, k)}" title="${t("elo.col.sort.title", { label: label.toLowerCase() })}">${label}${sortMark(sort, k)}</th>`;
  };

  el.innerHTML = `
    <h2 class="sec-title">${t("elo.section.title")} <span class="count">${t("elo.section.count")}</span></h2>
    <div class="card elo-card">
      <table class="elo-table">
        <thead>
          <tr>
            <th class="elo-th-rank" scope="col">#</th>
            <th class="elo-th-team" scope="col">${t("stats.col.team")}</th>
            ${th("elo", t("stats.col.elo"))}
            ${th("note", t("stats.col.rating"))}
            ${th("value", t("elo.col.value"))}
          </tr>
        </thead>
        <tbody>
          ${sorted
            .map((r, i) => {
              const cls = r.delta > 0 ? "up" : r.delta < 0 ? "down" : "flat";
              const arrow = r.delta > 0 ? "▲" : r.delta < 0 ? "▼" : "·";
              const deltaTxt = r.delta !== 0 ? `${arrow} ${Math.abs(r.delta)}` : arrow;
              return `
            <tr class="elo-row">
              <td class="elo-rank">${i + 1}</td>
              <td class="elo-team">${flagSpan(r.code)}${ADJUSTED.has(r.code) ? `<span class="elo-adj" title="${t("elo.adjusted.title")}">*</span>` : ""}</td>
              <td class="elo-val"><span class="elo-num">${r.elo}</span><span class="elo-delta ${cls}" title="${t("elo.delta.title")}">${deltaTxt}</span></td>
              <td class="elo-note">${r.note != null ? `<button type="button" class="elo-note-btn" data-rating-team="${r.code}" title="${t("elo.note.btn.title", { n: r.noteN })}">${r.note.toFixed(1)}</button>` : "—"}</td>
              <td class="elo-value">${r.value > 0 ? `<button type="button" class="elo-value-btn" data-value-team="${r.code}" title="${t("elo.value.btn.title")}">${fmtValue(r.value)}</button>` : fmtValue(r.value)}</td>
            </tr>`;
            })
            .join("")}
        </tbody>
        <tfoot>
          <tr class="elo-avg">
            <td></td>
            <td class="elo-team">${t("elo.avg.row")}</td>
            <td class="elo-val"><span class="elo-num">${avgElo}</span></td>
            <td class="elo-note">${avgNote != null ? avgNote.toFixed(1) : "—"}</td>
            <td class="elo-value">${fmtValue(avgValue)}</td>
          </tr>
        </tfoot>
      </table>
      <p class="note">${t("elo.note", { n: playedCount })}</p>
    </div>`;

  // Tri au clic ou au clavier (Entrée / Espace) sur un en-tête triable.
  el.onclick = (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>("[data-sort]");
    if (target?.dataset.sort) applySort(target.dataset.sort as SortKey);
  };
  el.onkeydown = (e) => {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
    const target = (e.target as HTMLElement).closest<HTMLElement>("[data-sort]");
    if (target?.dataset.sort) {
      e.preventDefault();
      applySort(target.dataset.sort as SortKey);
    }
  };
}
